import { useEffect, useRef } from 'react';
import type { SessionSnapshot } from '../game/session';
import type { HandOver } from './handOver';

interface Props {
  snapshot: SessionSnapshot;
  handOver: HandOver;
  onReady: () => void;
}

/**
 * The screen between two people at one device: who it goes to and why, and one button to say
 * they have it. It covers whatever the next person is about to see (a hand of support cards, the
 * board) so the last person does not see it on the way past. A pad answers it like any message;
 * the button is focused for Enter and Space.
 */
export function HandOverOverlay({ snapshot, handOver, onReady }: Props) {
  const faction = snapshot.ctx.board.def.factions.find((f) => f.id === handOver.faction);
  const side = faction?.name ?? handOver.faction;
  const person = snapshot.names[handOver.faction];
  const who = person ? `${person} (${side})` : side;
  const button = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    button.current?.focus();
  }, [handOver.key]);
  const line =
    handOver.reason === 'card'
      ? `${who} chooses a support card in secret.`
      : handOver.reason === 'muster'
        ? `${who} answers the muster.`
        : `${who} to move.`;
  return (
    <div className="card-battle hand-over" role="alertdialog" aria-label="Hand over the device">
      <section className="hand-over-panel" data-testid="hand-over">
        <p className="hand-over-title">Pass the device to</p>
        {/* In the text's colour: a dark side's own colour on this backdrop could not be read
            (client-tablets-ui:quality#11). Its colour underlines the name instead. */}
        <h1 style={faction ? { borderBottomColor: faction.color } : undefined}>{who}</h1>
        <p className="hint">{line}</p>
        <button ref={button} className="primary" data-testid="hand-over-ready" onClick={onReady}>
          {person ? `${person} is ready` : 'Ready'}
        </button>
      </section>
    </div>
  );
}
